import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  REPORTING_LE_LENS_SPACES_PATH,
  reportingLeLensSpaceMembersPath,
  reportingLeLensSpacePath,
} from '~/consts/reportingLeLensApi';

import useCurrentUser from '~/hooks/useCurrentUser';

import { request } from '~/services/common';

import type { LensLibraryAccess, LensSpace, LensSpaceMember } from '~/types/leLens';

import { encodeRequestBody } from '~/utils/encodeRequestBody';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { reportingLeLensQueryKeys } from './useGetReportingLeLens';

export const lensSpaceQueryKeys = {
  all: ['reporting', 'le-lens', 'spaces'] as const,
  lists: () => [...lensSpaceQueryKeys.all, 'list'] as const,
  browsable: () => [...lensSpaceQueryKeys.all, 'browsable'] as const,
  detail: (spaceId: string) => [...lensSpaceQueryKeys.all, 'detail', spaceId] as const,
  prefs: () => [...lensSpaceQueryKeys.all, 'prefs'] as const,
  suggestions: () => [...lensSpaceQueryKeys.all, 'suggestions'] as const,
};

type SpacesResponse = { spaces: Array<LensSpace>; libraryAccess: LensLibraryAccess };
type SpaceResponse = { space: LensSpace | null; members: Array<LensSpaceMember> };

/**
 * Every team space the signed-in employee belongs to, with its membership.
 *
 * Spaces they are not in are not returned: the library's Team spaces destination
 * is their spaces, not a directory of the company's.
 */
export function useGetLensSpaces(enabled = true) {
  const { user } = useCurrentUser();
  return useQuery({
    queryKey: [...lensSpaceQueryKeys.lists(), user?.email],
    enabled: enabled && Boolean(user?.email),
    queryFn: async ({ signal }) => {
      const response = (await request(resolveReportingLeLensUrl(REPORTING_LE_LENS_SPACES_PATH), {
        signal,
      })) as SpacesResponse;
      return response.spaces ?? [];
    },
    retry: false,
  });
}

/** Every folder the reader can browse (theirs, every open one, all of them for a reporting admin), and whether they may create one. */
export function useGetBrowsableLensSpaces(enabled = true) {
  const { user } = useCurrentUser();
  return useQuery({
    queryKey: [...lensSpaceQueryKeys.browsable(), user?.email],
    enabled: enabled && Boolean(user?.email),
    queryFn: async ({ signal }) => {
      const response = (await request(resolveReportingLeLensUrl(`${REPORTING_LE_LENS_SPACES_PATH}?all=true`), {
        signal,
      })) as Partial<SpacesResponse>;
      return {
        spaces: response.spaces ?? [],
        // Older reporting snapshots omit library permissions. Navigation remains readable;
        // reviewer and creation actions stay hidden until the server explicitly grants them.
        libraryAccess: response.libraryAccess ?? {
          canSeeEveryLens: false,
          canReviewCertification: false,
          canCreateFolder: false,
        },
      };
    },
    retry: false,
  });
}

/** One folder with its people, readable by anyone who can browse it. */
export function useGetLensSpace(spaceId: string | null) {
  return useQuery({
    queryKey: lensSpaceQueryKeys.detail(spaceId ?? ''),
    enabled: Boolean(spaceId),
    queryFn: async ({ signal }) =>
      (await request(resolveReportingLeLensUrl(reportingLeLensSpacePath(spaceId)), { signal })) as {
        space: LensSpace;
        members: Array<LensSpaceMember>;
      },
    retry: false,
  });
}

/** Invalidates spaces and the Lens list together: a space change moves rows between destinations. */
function useSpaceMutation<TInput>(
  mutationFn: (input: TInput) => Promise<SpaceResponse>,
  successMessage: (result: SpaceResponse) => string,
) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn,
    onSuccess: async (result) => {
      enqueueSnackbar(successMessage(result), { variant: 'success' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: lensSpaceQueryKeys.all }),
        queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() }),
      ]);
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'We couldn’t save this team space. Try again.', { variant: 'error' });
    },
  });
}

export function useCreateLensSpace() {
  return useSpaceMutation<{
    name: string;
    description: string | null;
    managers?: Array<string>;
    audience: LensSpace['audience'];
  }>(
    async (input) =>
      (await request(resolveReportingLeLensUrl(REPORTING_LE_LENS_SPACES_PATH), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encodeRequestBody(input)),
      })) as SpaceResponse,
    (result) => `${result.space?.name ?? 'Team space'} created`,
  );
}

export function useUpdateLensSpace() {
  return useSpaceMutation<{
    spaceId: string;
    name: string;
    description: string | null;
    audience: LensSpace['audience'];
  }>(
    async ({ spaceId, ...body }) =>
      (await request(resolveReportingLeLensUrl(reportingLeLensSpacePath(spaceId)), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encodeRequestBody(body)),
      })) as SpaceResponse,
    (result) => `${result.space?.name ?? 'Team space'} updated`,
  );
}

export type LensTeamPrefs = { mainTeamId: string | null; pinnedTeamIds: Array<string> };

export type LensTeamSuggestion = { space: LensSpace; reason: string };

/** Suggested active, open teams. The service returns an empty list if People lookup is unavailable. */
export function useGetLensTeamSuggestions(enabled = true) {
  const { user } = useCurrentUser();
  return useQuery({
    queryKey: [...lensSpaceQueryKeys.suggestions(), user?.email],
    enabled: enabled && Boolean(user?.email),
    queryFn: async ({ signal }) => {
      const response = (await request(resolveReportingLeLensUrl(`${REPORTING_LE_LENS_SPACES_PATH}/suggestions`), {
        signal,
      })) as { suggestions?: Array<LensTeamSuggestion> };
      return response.suggestions ?? [];
    },
    retry: false,
  });
}

export function useGetLensTeamPrefs(enabled = true) {
  const { user } = useCurrentUser();
  return useQuery({
    queryKey: [...lensSpaceQueryKeys.prefs(), user?.email],
    enabled: enabled && Boolean(user?.email),
    queryFn: async ({ signal }) =>
      (await request(resolveReportingLeLensUrl(`${REPORTING_LE_LENS_SPACES_PATH}/prefs`), { signal })) as LensTeamPrefs,
    retry: false,
  });
}

export function useGetLensTeamPeople(search: string, enabled = true) {
  return useQuery({
    queryKey: [...lensSpaceQueryKeys.all, 'people', search],
    enabled: enabled && search.trim().length >= 3,
    queryFn: async ({ signal }) => {
      const response = (await request(
        resolveReportingLeLensUrl(`${REPORTING_LE_LENS_SPACES_PATH}/people?q=${encodeURIComponent(search.trim())}`),
        { signal },
      )) as { people: Array<{ email: string; name: string }> };
      return response.people ?? [];
    },
    retry: false,
  });
}

export function useUpdateLensTeamPrefs() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: async (prefs: LensTeamPrefs) =>
      (await request(resolveReportingLeLensUrl(`${REPORTING_LE_LENS_SPACES_PATH}/prefs`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prefs),
      })) as LensTeamPrefs,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: lensSpaceQueryKeys.all }),
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'We couldn’t save your team choices. Try again.', { variant: 'error' });
    },
  });
}

export function useJoinLensSpace() {
  return useSpaceMutation<{ spaceId: string }>(
    async ({ spaceId }) =>
      (await request(resolveReportingLeLensUrl(`${reportingLeLensSpacePath(spaceId)}/join`), {
        method: 'POST',
      })) as SpaceResponse,
    () => 'You joined the team',
  );
}

export function useLeaveLensSpace() {
  return useSpaceMutation<{ spaceId: string }>(
    async ({ spaceId }) =>
      (await request(resolveReportingLeLensUrl(`${reportingLeLensSpacePath(spaceId)}/leave`), {
        method: 'POST',
      })) as SpaceResponse,
    () => 'You left the team',
  );
}

export function useArchiveLensSpace() {
  return useSpaceMutation<{ spaceId: string }>(
    async ({ spaceId }) =>
      (await request(resolveReportingLeLensUrl(`${reportingLeLensSpacePath(spaceId)}/archive`), {
        method: 'POST',
      })) as SpaceResponse,
    () => 'Team archived',
  );
}

export function useAddLensSpaceMember() {
  return useSpaceMutation<{ spaceId: string; email: string; role: LensSpaceMember['role'] }>(
    async ({ spaceId, ...body }) =>
      (await request(resolveReportingLeLensUrl(reportingLeLensSpaceMembersPath(spaceId)), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })) as SpaceResponse,
    () => 'Person added',
  );
}

export function usePatchLensSpaceMember() {
  return useSpaceMutation<{ spaceId: string; email: string; role: LensSpaceMember['role'] }>(
    async ({ spaceId, email, role }) =>
      (await request(
        resolveReportingLeLensUrl(`${reportingLeLensSpaceMembersPath(spaceId)}/${encodeURIComponent(email)}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role }),
        },
      )) as SpaceResponse,
    () => 'Role updated',
  );
}

export function useRemoveLensSpaceMember() {
  return useSpaceMutation<{ spaceId: string; email: string }>(
    async ({ spaceId, email }) =>
      (await request(
        resolveReportingLeLensUrl(`${reportingLeLensSpaceMembersPath(spaceId)}/${encodeURIComponent(email)}`),
        {
          method: 'DELETE',
        },
      )) as SpaceResponse,
    () => 'Person removed',
  );
}
